import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getMyTimetable, type TimetableLesson } from '../../lib/functions';
import { fetchCalendarItems, CAL_MODULE_META, type CalItem } from '../../lib/schoolCalendar';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const fmtTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' }) : '';
const fmtDate = (d: Date): string => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * The home dashboard's calendar: today's lessons from the MIS timetable plus the
 * next handful of school events and deadlines. Each half stays quiet when it has
 * nothing, and the whole thing degrades gracefully when a school has no MIS or
 * no subscribed calendar.
 */
export default function HomeCalendar() {
  const { appUser } = useAuth();
  const schoolId = appUser?.schoolId ?? '';
  const isLeader = appUser?.role === 'schoolAdmin' || appUser?.role === 'headteacher';

  const [todayLessons, setTodayLessons] = useState<TimetableLesson[] | null>(null);
  const [upcoming, setUpcoming] = useState<CalItem[] | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    let live = true;
    const todayName = DAY_NAMES[new Date().getDay()];
    getMyTimetable({ weekOffset: 0 })
      .then((res) => { if (live) setTodayLessons(res.data.lessons.filter((l) => l.day === todayName)); })
      .catch(() => { if (live) setTodayLessons([]); });
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    fetchCalendarItems(schoolId, isLeader)
      .then((items) => {
        if (!live) return;
        setUpcoming(items.filter((i) => i.date >= startOfToday).sort((a, b) => a.date.getTime() - b.date.getTime()).slice(0, 6));
      })
      .catch(() => { if (live) setUpcoming([]); });
    return () => { live = false; };
  }, [schoolId, isLeader]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Today's lessons */}
      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Today's lessons</h3>
          <Link to="/timetable" className="text-xs font-medium text-brand-600 hover:underline">Full timetable →</Link>
        </div>
        {todayLessons === null ? (
          <p className="py-4 text-center text-sm text-gray-300">Loading…</p>
        ) : todayLessons.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">No lessons today.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {todayLessons.map((l, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <span className="w-24 shrink-0 text-sm font-medium text-gray-700">{fmtTime(l.start)}{l.end ? `–${fmtTime(l.end)}` : ''}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{l.className}</span>
                {l.room && <span className="badge bg-gray-100 text-gray-600">Rm {l.room}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming events & deadlines */}
      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Coming up</h3>
          {/* /calendar is staff-only — don't offer pupils a link that bounces. */}
          {appUser?.role !== 'student' && (
            <Link to="/calendar" className="text-xs font-medium text-brand-600 hover:underline">Calendar →</Link>
          )}
        </div>
        {upcoming === null ? (
          <p className="py-4 text-center text-sm text-gray-300">Loading…</p>
        ) : upcoming.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">Nothing coming up.</p>
        ) : (
          <ul className="space-y-2.5">
            {upcoming.map((e) => (
              <li key={e.id} className="flex items-start gap-3">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${CAL_MODULE_META[e.module].dot}`} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-gray-800">{e.label}</span>
                  <span className="text-xs text-gray-400">{fmtDate(e.date)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
